import SequentialContainer from './Base';
import { RandomIterator } from "./Base/RandomIterator";
import $checkWithinAccessParams from "../../utils/checkParams.macro";
class VectorIterator extends RandomIterator {
    constructor(node, container, iteratorType) {
        super(node, iteratorType);
        this.container = container;
    }
    copy() {
        return new VectorIterator(this._node, this.container, this.iteratorType);
    }
}
class Vector extends SequentialContainer {
    /**
     * @param container - Initialize container, must have a forEach function.
     * @param copy - When the container is an array, you can choose to directly operate on the original object of
     *               the array or perform a shallow copy. The default is shallow copy.
     */
    constructor(container = [], copy = true) {
        super();
        if (Array.isArray(container)) {
            this._vector = copy ? [...container] : container;
            this._length = container.length;
        }
        else {
            this._vector = [];
            const self = this;
            container.forEach(function (el) {
                self.pushBack(el);
            });
        }
    }
    clear() {
        this._length = 0;
        this._vector.length = 0;
    }
    begin() {
        return new VectorIterator(0, this);
    }
    end() {
        return new VectorIterator(this._length, this);
    }
    rBegin() {
        return new VectorIterator(this._length - 1, this, 1 /* IteratorType.REVERSE */);
    }
    rEnd() {
        return new VectorIterator(-1, this, 1 /* IteratorType.REVERSE */);
    }
    front() {
        return this._vector[0];
    }
    back() {
        return this._vector[this._length - 1];
    }
    getElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        return this._vector[pos];
    }
    eraseElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        this._vector.splice(pos, 1);
        this._length -= 1;
        return this._length;
    }
    eraseElementByValue(value) {
        let index = 0;
        for (let i = 0; i < this._length; ++i) {
            if (this._vector[i] !== value) {
                this._vector[index++] = this._vector[i];
            }
        }
        this._length = this._vector.length = index;
        return this._length;
    }
    eraseElementByIterator(iter) {
        const _node = iter._node;
        iter = iter.next();
        this.eraseElementByPos(_node);
        return iter;
    }
    pushBack(element) {
        this._vector.push(element);
        this._length += 1;
        return this._length;
    }
    popBack() {
        if (this._length === 0)
            return;
        this._length -= 1;
        return this._vector.pop();
    }
    setElementByPos(pos, element) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        this._vector[pos] = element;
    }
    insert(pos, element, num = 1) {
        if (pos < 0 || pos > this._length) {
            throw new RangeError();
        }
        this._vector.splice(pos, 0, ...new Array(num).fill(element));
        this._length += num;
        return this._length;
    }
    find(element) {
        for (let i = 0; i < this._length; ++i) {
            if (this._vector[i] === element) {
                return new VectorIterator(i, this);
            }
        }
        return this.end();
    }
    reverse() {
        this._vector.reverse();
    }
    unique() {
        let index = 1;
        for (let i = 1; i < this._length; ++i) {
            if (this._vector[i] !== this._vector[i - 1]) {
                this._vector[index++] = this._vector[i];
            }
        }
        this._length = this._vector.length = index;
        return this._length;
    }
    sort(cmp) {
        this._vector.sort(cmp);
    }
    forEach(callback) {
        for (let i = 0; i < this._length; ++i) {
            callback(this._vector[i], i, this);
        }
    }
    [Symbol.iterator]() {
        return function* () {
            yield* this._vector;
        }.bind(this)();
    }
}
export default Vector;
